import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, Observable, forkJoin, of, timer } from 'rxjs';
import { catchError, concatMap, filter, first, map, switchMap, tap } from 'rxjs/operators';

import { AuthenticationService } from 'app/auth/service';
import { EComProduct } from './modelo/product';
import { ApiService } from 'app/modulos/api.service';
import { Cardapio, CardapioItem } from './modelo/cardapio';
import { cardapioSemDestaques, cardapioSemVazias } from './modelo/cardapio-filtro';
import { cardapioRota, checkoutRota, pedidosRota, perfilRota } from './modelo/rota';
import { Contexto, contextoMudou } from './modelo/contexto';
import {
  LojaServidor, LojaSlug, lojaSlugCatalogoEncontra, lojaSlugDeServidor, slugNormaliza,
} from 'app/modulos/administrativo/estabelecimento/loja-slug';
import {
  PedidoResumo,
  historicoAdiciona,
  historicoUne,
  pedidoPlanilhaParaResumo,
} from './modelo/pedido-resumo';
import { ClientePedido, clientePedidoMonta } from './modelo/cliente-pedido';
import { Empresa } from 'app/modulos/administrativo/empresa/empresa';
import { VisualIdentidade } from 'app/modulos/administrativo/empresa/visual-identidade';
import { Cartao } from 'app/modulos/movimento/pagamento/cartao';
import { Voucher } from 'app/modulos/venda/voucher/voucher';
import { voucherCatalogoEncontra } from 'app/modulos/venda/voucher/voucher-catalogo';
import {
  Estabelecimento, estabelecimentoEnderecoOrigem, estabelecimentoEnderecoQueries,
} from 'app/modulos/administrativo/estabelecimento/estabelecimento';
import { Sacola, SacolaCliente, SacolaLinha } from './modelo/sacola';
import { Localizador } from 'app/modulos/venda/localizador/localizador';
import { Pedido, PedidoModelo, PedidoSituacao, PedidoTipo } from 'app/modulos/integracao/pedido/pedido';
import { pedidoNegadaMotivo, pedidoSituacaoNegada } from 'app/modulos/integracao/pedido/pedido-status';
import { PedidoItem } from 'app/modulos/integracao/pedido/pedido-item';
import {
  PedidoPlanilha, pedidoPlanilhaConsulta, pedidoPlanilhaLista,
} from 'app/modulos/integracao/pedido/pedido-planilha';
import { Item } from 'app/modulos/recurso/item/item';
import { Cliente } from 'app/modulos/venda/localizador/cliente/cliente';
import {
  AgenteCliente,
  AgenteClienteContato,
  AgenteClienteNovo,
  agenteClienteAlergenoCorpo,
  agenteClienteContato,
  agenteEnderecosParaAgenda,
  agenteNumeroServidor,
  enderecoParaAgente,
} from 'app/modulos/venda/localizador/cliente/agente-cliente';
import {
  Endereco, cepDigitos, enderecoMunicipioEntidade, enderecoUfEntidade,
} from 'app/modulos/venda/entrega/endereco';
import { Coordenada, TaxaEntregaConfig, taxaEntregaConfigPadrao } from 'app/modulos/venda/entrega/taxa-entrega';
import { taxaEntregaCalcula } from 'app/modulos/venda/entrega/taxa-entrega-calculo';
import {
  TaxaEntregaPanorama, taxaEntregaPanoramaConverte, taxaEntregaPanoramaValido,
} from 'app/modulos/venda/entrega/taxa-entrega-panorama';
import { CepConsulta, CepResultado, Municipio, Uf, ufListaEntrega } from 'app/modulos/venda/entrega/uniao';
import {
  GeoCoordenadas, GeoCoordenadasConsulta, geoConsultaDeEndereco, geoConsultaMonta, geoConsultaParametros, geoCoordenada,
} from 'app/modulos/venda/entrega/geo-coordenadas';
import { ClienteAlergenoPerfil, clienteAlergenoPerfil } from 'app/modulos/venda/localizador/cliente/cliente-alergeno';
import { LocalizacaoEndereco, localizacaoDePhoton } from 'app/modulos/venda/entrega/localizacao';
import { EntregaTipo, FormaPagamento } from 'app/modulos/movimento/pagamento/pagamento';
import { EstabelecimentoUnidade, unidadePagamentos } from 'app/modulos/administrativo/estabelecimento/estabelecimento-unidade';
import { FuncionamentoSituacao, funcionamentoSituacao } from 'app/modulos/administrativo/estabelecimento/estabelecimento-funcionamento';

// O backend exige responsavel; 10 e o valor que ele aceita. Demais valores do enum: a levantar.
const ENTREGA_RESPONSAVEL = 10;
// `local` NAO e o numero do endereco: e o enum PedidoEntregaLocal da aps (Estabelecimento 10,
// Destinatario 30), que a APL le como area da entrega. O numero vai em `numero`.
const ENTREGA_LOCAL_DESTINATARIO = 30;

@Injectable({
  providedIn: 'root'
})
export class EcommerceService implements Resolve<any> {
  // Public
  empresa: Empresa;
  estabelecimento: Estabelecimento;
  localizador: Localizador;
  // `endereco` e o EM USO; `enderecos` e a agenda do cliente.
  endereco: Endereco;
  enderecos: Endereco[] = [];
  cartoes: Cartao[] = [];
  frete: number = 0.00;
  foraDeArea: boolean = false;
  freteCalculando: boolean = false;
  taxaConfig: TaxaEntregaConfig;
  visual: VisualIdentidade;
  unidade: EstabelecimentoUnidade;
  // UF e municipio nao mudam na sessao. Municipio e por codigo de UF.
  private ufs: Uf[] = null;
  private municipios: { [ufCodigo: string]: Municipio[] } = {};
  // Recarrega so quando a identidade muda: `currentUser` emite sem trocar de pessoa.
  private clienteCarregado: string = null;
  // Cliente do servidor (agente/cliente), com a agenda de enderecos e cartoes.
  agenteCliente: AgenteCliente = null;
  // Trava contra o `resolve()` e o login dispararem dois GET-senao-POST: o segundo POST
  // criaria o cliente de novo.
  private agenteGarantido: string = null;
  // Detalhe de pedido por id. Ver `pedidoDetalheObtem`.
  private pedidoDetalhes: { [id: string]: Pedido } = {};
  // Id do ultimo pedido gravado pelo `confirma()`: a tela de enviado acompanha este.
  pedidoEnviadoId: string = null;
  sacola: Sacola;
  cardapio: Cardapio;
  productList: Array<EComProduct>;
  wishlist: Array<any>;
  cartList: Array<any>;
  selectedProduct;
  relatedProducts;

  onEmpresaChange: BehaviorSubject<any>;
  onEstabelecimentoChange: BehaviorSubject<any>;
  onLocalizadorChange: BehaviorSubject<any>;
  onEnderecoChange: BehaviorSubject<any>;
  onEnderecosChange: BehaviorSubject<any>;
  onCartoesChange: BehaviorSubject<any>;
  onAgenteClienteChange: BehaviorSubject<any>;
  onFreteChange: BehaviorSubject<any>;
  onTaxaConfigChange: BehaviorSubject<any>;
  onVisualChange: BehaviorSubject<any>;
  onUnidadeChange: BehaviorSubject<any>;
  onCardapioChange: BehaviorSubject<any>;
  onSacolaChange: BehaviorSubject<any>;
  onProductListChange: BehaviorSubject<any>;
  onRelatedProductsChange: BehaviorSubject<any>;
  onWishlistChange: BehaviorSubject<any>;
  onCartListChange: BehaviorSubject<any>;
  onSelectedProductChange: BehaviorSubject<any>;

  // Private
  private idHandel;
  private empresaId: string = '';
  private estabelecimentoId: string = '';
  private localizadorId: string = '';
  // Preenchido quando o cliente entrou por `/loja/{slug}`; a navegacao interna segue por ele.
  public slug: string = '';

  private sortRef = key => (a, b) => {
    const fieldA = a[key];
    const fieldB = b[key];

    let comparison = 0;
    if (fieldA > fieldB) {
      comparison = 1;
    } else if (fieldA < fieldB) {
      comparison = -1;
    }
    return comparison;
  };

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   */
  constructor(
    private _httpClient: HttpClient,
    private apiService: ApiService,
    private authService: AuthenticationService,
    private _router: Router,
  ) {
    this.sacola = new Sacola();
    this.onEmpresaChange = new BehaviorSubject({});
    this.onEstabelecimentoChange = new BehaviorSubject({});
    this.onLocalizadorChange = new BehaviorSubject({});
    this.onEnderecoChange = new BehaviorSubject({});
    this.onEnderecosChange = new BehaviorSubject([]);
    this.onCartoesChange = new BehaviorSubject([]);
    this.onAgenteClienteChange = new BehaviorSubject(null);
    this.onFreteChange = new BehaviorSubject({});
    this.onTaxaConfigChange = new BehaviorSubject({});
    this.onVisualChange = new BehaviorSubject({});
    this.onUnidadeChange = new BehaviorSubject(null);
    this.onCardapioChange = new BehaviorSubject({});
    this.onSacolaChange = new BehaviorSubject({});
    this.onProductListChange = new BehaviorSubject({});
    this.onRelatedProductsChange = new BehaviorSubject({});
    this.onWishlistChange = new BehaviorSubject({});
    this.onCartListChange = new BehaviorSubject({});
    this.onSelectedProductChange = new BehaviorSubject({});
    this.sacola.inicia();

    // O `resolve()` le endereco e cartao, mas o cliente loga DEPOIS - o botao do Google fica
    // dentro do checkout. Sem isto, a lista lida como anonimo fica na tela.
    this.authService.currentUser.subscribe(() => {
      const cliente = this.clienteChave();
      if (cliente === this.clienteCarregado) {
        return;
      }
      this.clienteCarregado = cliente;
      if (!this.estabelecimentoId) {
        return;
      }
      this.enderecoCarrega();
      this.cartoesCarrega();
      this.agenteClienteGarante();
    });
  }

  /**
   * Resolver
   *
   * @param {ActivatedRouteSnapshot} route
   * @param {RouterStateSnapshot} state
   * @returns {Observable<any> | Promise<any> | any}
   */
  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Promise<any> | any {
    this.idHandel = route.params.id;
    this.isConsulta = !!route.data?.consulta;
    // `/loja/{slug}`: traduz o slug para os ids ANTES de carregar, e dai segue igual a
    // rota com ids. Slug desconhecido vai para a pagina de nao encontrado.
    if (route.params.slug) {
      return new Promise<void>((resolve, reject) => {
        this.lojaIdentifica(route.params.slug).subscribe((loja: LojaSlug | null) => {
          if (!loja) {
            this._router.navigate(['/pages/miscellaneous/error']);
            resolve();
            return;
          }
          this.slug = loja.slug;
          this.contextoAbre({
            empresaId: loja.empresaId,
            estabelecimentoId: loja.estabelecimentoId,
            localizadorId: route.params.localizadorid,
          });
          this.dadosCarrega().then(resolve, reject);
        }, reject);
      });
    }
    const hasContext = !!(route.params.empresaid || route.params.estabelecimentoid || route.params.localizadorid);
    if (hasContext) {
      // Entrou pelos ids: os links internos voltam a ser por id.
      this.slug = '';
      this.contextoAbre({
        empresaId: route.params.empresaid,
        estabelecimentoId: route.params.estabelecimentoid,
        localizadorId: route.params.localizadorid,
      });
    }
    return this.dadosCarrega();
  }

  private contextoAbre(novo: Contexto): void {
    // Zerar a sacola em toda rota com parametro esvaziava o checkout: o cardapio e o
    // checkout do mesmo estabelecimento carregam os mesmos parametros. So comeca de
    // novo quem entrou em outro contexto, como ler o QR de outra mesa.
    const comecaDeNovo = contextoMudou(
      { empresaId: this.empresaId, estabelecimentoId: this.estabelecimentoId, localizadorId: this.localizadorId },
      novo,
    );
    this.empresaId = novo.empresaId;
    this.estabelecimentoId = novo.estabelecimentoId;
    this.localizadorId = novo.localizadorId;
    if (comecaDeNovo || !this.sacola) {
      this.sacola = new Sacola();
    }
    this.onSacolaChange.next(this.sacola);
    this.taxaEntregaConfigCarrega();
    // Assincrono: a tela nao espera a taxa para abrir.
    this.taxaEntregaPanoramaCarrega().subscribe();
    this.visualCarrega();
    this.unidadeCarrega().subscribe();
    this.enderecoCarrega();
    // Recarregar a pagina com sessao ativa nao passa pelo login, entao sem isto a agenda
    // do servidor nunca chegaria.
    this.agenteClienteGarante();
  }

  // Carga comum as duas entradas (ids ou slug), depois que o contexto ja esta aberto.
  private dadosCarrega(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      Promise.all([this.getEmpresa(), this.getEstabelecimento(), this.getLocalizador(), this.getCardapio(), this.getWishlist(), this.getCartList(), this.getSelectedProduct()]).then(() => {
        resolve();
      }, reject);
    });
  }

  getEmpresa(): Promise<{ ok: boolean }> {
    if (!this.empresaId) {
      this.empresa = null;
      return Promise.resolve({ ok: false });
    }
    return new Promise((resolve, reject) => {
      this.apiService.encontra<Empresa>(`aps://integracao/cardapio/empresa/${this.empresaId}`, '').subscribe((empresa: Empresa) => {
        this.empresa = empresa;
        // Override local de foto/capa/cor, quando quem abriu a loja e quem esta
        // configurando. Sem override, o que vale e o que veio do backend.
        this.visualAplica(empresa);
        this.onEmpresaChange.next(empresa);
        resolve({ ok: true });
      }, reject);
    });
  }

  getEstabelecimento(): Promise<{ ok: boolean }> {
    if (!this.estabelecimentoId) {
      this.estabelecimento = null;
      return Promise.resolve({ ok: false });
    }
    return new Promise((resolve, reject) => {
      this.apiService.encontra<Estabelecimento>(`aps://integracao/cardapio/estabelecimento/${this.estabelecimentoId}`, '').subscribe((estabelecimento: Estabelecimento) => {
        this.estabelecimento = estabelecimento;
        this.onEstabelecimentoChange.next(estabelecimento);
        this.estabelecimentoOrigemCarrega();
        resolve({ ok: true });
      }, reject);
    });
  }

  estabelecimentoOrigemCarrega(): void {
    this.estabelecimentoOrigemObtem().subscribe(coord => {
      if (coord) {
        this.origemAplica(coord);
      }
    });
  }

  // Backend primeiro (`integracao/geo/coordenadas`); o Photon fica de reserva.
  estabelecimentoOrigemObtem(): Observable<Coordenada | null> {
    const origem = estabelecimentoEnderecoOrigem(this.estabelecimento);
    const queries = estabelecimentoEnderecoQueries(origem);
    const consulta = origem
      ? geoConsultaMonta(origem.cep, origem.uf?.sigla, origem.municipio?.nome, origem.logradouro, origem.numero)
      : null;
    if (!queries.length && !consulta) {
      return of(null);
    }
    const cache = this.apiService.getStorageData('delivery', this.origemChave());
    if (cache) {
      return of(cache as Coordenada);
    }
    return this.geocodificaConstel(consulta).pipe(
      concatMap(coord => (coord ? of(coord) : this.geocodificaPrimeira(queries))),
      tap(coord => {
        if (coord) {
          this.apiService.setStorageData('delivery', this.origemChave(), coord);
        }
      }),
    );
  }

  estabelecimentoEnderecoTexto(): string {
    const endereco = estabelecimentoEnderecoOrigem(this.estabelecimento);
    if (!endereco) {
      return '';
    }
    const numero = endereco.numero ? `, ${endereco.numero}` : '';
    const partes = [
      `${endereco.logradouro || ''}${numero}`,
      endereco.bairro,
      [endereco.municipio?.nome, endereco.uf?.sigla].filter(Boolean).join('/'),
    ];
    return partes.filter(p => p && p.trim()).join(' - ');
  }

  taxaEntregaConfigExiste(): boolean {
    return !!this.apiService.getStorageData('delivery', this.taxaChave());
  }

  // Sequencial: o provedor limita a 1 req/s, e isto roda uma vez por estabelecimento.
  private geocodificaPrimeira(queries: string[]): Observable<Coordenada | null> {
    if (!queries.length) {
      return of(null);
    }
    const [primeira, ...resto] = queries;
    return this.geocodifica(primeira).pipe(
      concatMap(coord => (coord ? of(coord) : this.geocodificaPrimeira(resto))),
    );
  }

  private origemChave(): string {
    return `geo.constel.origem.${this.estabelecimentoId}`;
  }

  private origemAplica(origem: Coordenada): void {
    const config = this.taxaConfig || this.taxaEntregaConfigCarrega();
    config.origem = origem;
    this.onTaxaConfigChange.next(config);
    this.freteRecalcula();
  }

  getLocalizador(): Promise<{ ok: boolean }> {
    if (!this.localizadorId) {
      this.localizador = null;
      this.onLocalizadorChange.next(this.localizador);
      return Promise.resolve({ ok: false });
    }
    return new Promise((resolve, reject) => {
      this.apiService.encontra<Localizador>(`aps://integracao/cardapio/localizador/${this.localizadorId}`, '').subscribe((localizador: Localizador) => {
        this.localizador = localizador;
        this.onLocalizadorChange.next(localizador);
        resolve({ ok: true });
      }, reject);
    });
  }

  get isDelivery(): boolean {
    return !this.localizadorId;
  }

  // Cardapio so para ver (`cardapio/consulta`): sem sacola, sem entrega, sem pedido.
  public isConsulta = false;

  contextoDefine(empresaId: string, estabelecimentoId: string): void {
    this.empresaId = empresaId;
    this.estabelecimentoId = estabelecimentoId;
  }

  // Frete calculado pelo motor unificado. Mantem a assinatura usada pelo checkout e pelo confirma().
  getFreteEntrega(): number {
    return this.frete;
  }

  private taxaChave(): string {
    return `taxa.${this.empresaId}.${this.estabelecimentoId}`;
  }

  taxaEntregaConfigCarrega(): TaxaEntregaConfig {
    const dado = this.apiService.getStorageData('delivery', this.taxaChave());
    this.taxaConfig = dado ? (dado as TaxaEntregaConfig) : taxaEntregaConfigPadrao();
    this.onTaxaConfigChange.next(this.taxaConfig);
    return this.taxaConfig;
  }

  taxaEntregaConfigSalva(config: TaxaEntregaConfig): void {
    this.taxaConfig = config;
    this.apiService.setStorageData('delivery', this.taxaChave(), config);
    this.onTaxaConfigChange.next(this.taxaConfig);
  }

  // Faixas de frete da retaguarda. O config local vira fallback: quando o backend
  // responde, ele manda.
  taxaEntregaPanoramaCarrega(): Observable<TaxaEntregaConfig | null> {
    if (!(this.empresaId && this.estabelecimentoId)) {
      return of(null);
    }
    // `_httpClient` direto: `apiService.encontra` dispara toast tecnico de 24s em qualquer
    // falha, e isto roda em todo carregamento da loja.
    const url = this.apiService.getMapping(
      `aps://integracao/panorama/taxaentrega/${this.empresaId}/${this.estabelecimentoId}`);
    return this._httpClient
      .get<TaxaEntregaPanorama>(url)
      .pipe(
        map(panorama => {
          if (!taxaEntregaPanoramaValido(panorama)) {
            return null;
          }
          const config = taxaEntregaPanoramaConverte(panorama, this.taxaConfig || this.taxaEntregaConfigCarrega());
          this.taxaConfig = config;
          this.onTaxaConfigChange.next(config);
          this.freteRecalcula();
          return config;
        }),
        catchError(() => of(null)),
      );
  }

  // ---------- cartoes salvos ----------

  private cartoesChave(): string {
    return `cartoes.${this.empresaId}.${this.estabelecimentoId}.${this.clienteChave()}`;
  }

  cartoesCarrega(): Cartao[] {
    const dado = this.apiService.getStorageData('pagamento', this.cartoesChave());
    this.cartoes = Array.isArray(dado)
      ? dado.map(item => Object.assign(new Cartao(), item))
      : [];
    this.onCartoesChange.next(this.cartoes);
    return this.cartoes;
  }

  cartaoSalva(cartao: Cartao): void {
    if (!cartao.id) {
      cartao.id = Date.now().toString();
    }
    const i = this.cartoes.findIndex(c => c.id === cartao.id);
    if (i >= 0) {
      this.cartoes[i] = cartao;
    } else {
      this.cartoes = [...this.cartoes, cartao];
    }
    this.apiService.setStorageData('pagamento', this.cartoesChave(), this.cartoes);
    this.onCartoesChange.next(this.cartoes);
  }

  cartaoRemove(id: string): void {
    this.cartoes = this.cartoes.filter(c => c.id !== id);
    this.apiService.setStorageData('pagamento', this.cartoesChave(), this.cartoes);
    this.onCartoesChange.next(this.cartoes);
  }

  // ---------- cupom ----------
  //
  // Assinatura ja no formato final. Quando existir `aps://integracao/voucher/{codigo}`,
  // so o corpo deste metodo muda:
  //   return this.apiService.encontra<Voucher>(`aps://integracao/voucher/${codigo}`, '',
  //     this.pedidoCabecalhos());
  // Nenhuma tela e tocada. Codigo inexistente OU fora da validade devolve null, do mesmo
  // jeito que o endpoint devera responder 404 nos dois casos (nao vale entregar um
  // oraculo para adivinhar codigos).
  // Slug da URL -> empresa e estabelecimento, pelo `venda/loja` do servidor. `_httpClient`
  // pelo mesmo motivo do CEP: slug inexistente e 404, e virar toast tecnico seria pior que a
  // pagina de nao encontrado. Falhou, tenta o catalogo local antes de desistir.
  lojaIdentifica(slug: string): Observable<LojaSlug | null> {
    const chave = slugNormaliza(slug);
    if (!chave) {
      return of(null);
    }
    return this._httpClient.get<LojaServidor>(this.apiService.getMapping(`aps://venda/loja/${chave}`)).pipe(
      map(loja => lojaSlugDeServidor(loja) ?? lojaSlugCatalogoEncontra(chave)),
      catchError(() => of(lojaSlugCatalogoEncontra(chave))),
    );
  }

  voucherBusca(codigo: string): Observable<Voucher | null> {
    const voucher = voucherCatalogoEncontra(codigo);
    if (!voucher || !voucher.validoEm(new Date())) {
      return of(null);
    }
    return of(voucher);
  }

  // ---------- identidade visual (foto e capa da loja) ----------
  //
  // A LEITURA ja e real: os tres campos vem do backend no `getEmpresa()`.
  // O que e local e so o OVERRIDE de quem esta configurando, porque nao existe
  // caminho de escrita para a loja (`arquivo/envia` da 404 no `sirius-s`).
  // Consequencia que importa: o override NAO CHEGA NO CLIENTE - e a mesma
  // armadilha ja documentada da config de taxa. Por isso o override so pisa por
  // cima quando esta preenchido, e o cliente continua vendo o valor real.

  private visualChave(): string {
    return `visual.${this.empresaId}`;
  }

  visualCarrega(): VisualIdentidade {
    const dado = this.apiService.getStorageData('visual', this.visualChave());
    this.visual = Object.assign(new VisualIdentidade(), dado || {});
    this.onVisualChange.next(this.visual);
    return this.visual;
  }

  visualSalva(visual: VisualIdentidade): void {
    this.visual = Object.assign(new VisualIdentidade(), visual);
    this.apiService.setStorageData('visual', this.visualChave(), this.visual);
    if (this.empresa) {
      this.visualAplica(this.empresa);
      this.onEmpresaChange.next(this.empresa);
    }
    this.onVisualChange.next(this.visual);
  }

  // ---------- unidade: endereco, horario e pagamentos ----------
  //
  // Mock com a assinatura final: o backend nao tem onde guardar horario nem pagamentos.
  // Mesma armadilha da config de taxa e do visual - grava no navegador de quem configura e
  // NAO CHEGA NO CLIENTE em outro aparelho. Sem nada salvo, a loja segue como antes:
  // sempre aberta e com as quatro formas de pagamento.

  private unidadeChave(): string {
    return `unidade.${this.empresaId}.${this.estabelecimentoId}`;
  }

  unidadeCarrega(): Observable<EstabelecimentoUnidade> {
    const dado = this.apiService.getStorageData('delivery', this.unidadeChave());
    this.unidade = dado ? Object.assign(new EstabelecimentoUnidade(), dado) : null;
    this.onUnidadeChange.next(this.unidade);
    return of(this.unidade);
  }

  unidadeSalva(unidade: EstabelecimentoUnidade): Observable<EstabelecimentoUnidade> {
    this.unidade = Object.assign(new EstabelecimentoUnidade(), unidade);
    this.apiService.setStorageData('delivery', this.unidadeChave(), this.unidade);
    this.onUnidadeChange.next(this.unidade);
    return of(this.unidade);
  }

  funcionamentoSituacao(agora = new Date()): FuncionamentoSituacao | null {
    return funcionamentoSituacao(this.unidade?.funcionamento, agora);
  }

  get lojaAberta(): boolean {
    const situacao = this.funcionamentoSituacao();
    return !situacao || situacao.aberto;
  }

  get pagamentosAceitos(): FormaPagamento[] {
    return unidadePagamentos(this.unidade);
  }

  // Pisa por cima do que veio do backend, campo a campo. Campo vazio no override
  // nao apaga o valor real - so substitui o que foi de fato preenchido.
  visualAplica(empresa: Empresa): void {
    const visual = this.visual;
    if (!empresa || !visual || !visual.preenchida()) {
      return;
    }
    if (visual.foto) {
      empresa.imagem = visual.foto;
    }
    if (!visual.capa && !visual.cor) {
      return;
    }
    const configuracao: any = empresa.empresaConfiguracao || ({} as any);
    configuracao.parametros = configuracao.parametros || {};
    configuracao.parametros.visual = configuracao.parametros.visual || {};
    const bloco = configuracao.parametros.visual;
    if (visual.cor) {
      bloco.cor = visual.cor;
    }
    if (visual.capa) {
      bloco.cardapio = bloco.cardapio || {};
      bloco.cardapio.faixa = visual.capa;
    }
    empresa.empresaConfiguracao = configuracao;
  }

  // Resumo do que a loja mostra hoje, ja com o override aplicado. A tela de
  // configuracao usa isto para pre-carregar os campos com o valor real.
  visualEfetivo(): VisualIdentidade {
    const empresa: any = this.empresa;
    const efetivo = new VisualIdentidade();
    efetivo.foto = empresa?.imagem || '';
    efetivo.capa = empresa?.empresaConfiguracao?.parametros?.visual?.cardapio?.faixa || '';
    efetivo.cor = empresa?.empresaConfiguracao?.parametros?.visual?.cor || '';
    return efetivo;
  }

  // Recalcula o frete do endereco atual e publica em onFreteChange.
  // Fora do delivery (mesa) ou sem endereco => frete zero.
  freteRecalcula(): void {
    if (!this.isDelivery || !this.endereco) {
      this.frete = 0.00;
      this.foraDeArea = false;
      this.freteCalculando = false;
      this.onFreteChange.next({ frete: this.frete, foraDeArea: this.foraDeArea, calculando: false });
      return;
    }
    const config = this.taxaConfig || this.taxaEntregaConfigCarrega();
    this.freteCalculando = true;
    this.onFreteChange.next({ frete: this.frete, foraDeArea: this.foraDeArea, calculando: true });
    this.enderecoGeocodifica(this.endereco).subscribe(coord => {
      this.endereco.latitude = coord.latitude;
      this.endereco.longitude = coord.longitude;
      const resultado = taxaEntregaCalcula(config, coord, this.sacola.total);
      this.frete = resultado.dentroDaArea ? resultado.valor : 0.00;
      this.foraDeArea = !resultado.dentroDaArea;
      this.freteCalculando = false;
      this.onFreteChange.next({
        frete: this.frete, foraDeArea: this.foraDeArea, calculando: false,
        distanciaKm: resultado.distanciaKm,
      });
    });
  }

  // Geocoding do endereco: backend (`integracao/geo/coordenadas`), depois Photon, e por
  // ultimo o mock deterministico (derivado do CEP) quando os dois falharem.
  // Assinatura final Observable para trocar de provedor sem mexer na UI.
  enderecoGeocodifica(endereco: Endereco): Observable<Coordenada> {
    const cepNorm = (endereco.cep || '').replace(/\D/g, '');
    // Prefixo `geo.constel`: a chave antiga guardava o ponto do Photon, e manter a mesma
    // faria o cache esconder o backend de quem ja tinha calculado frete.
    const cacheChave = `geo.constel.${cepNorm}.${(endereco.numero || '').trim()}`;
    const cache = this.apiService.getStorageData('delivery', cacheChave);
    if (cache) {
      return of(cache as Coordenada);
    }
    const q = [endereco.logradouro, endereco.numero, endereco.bairro, endereco.cidade, endereco.uf, 'Brasil']
      .filter(Boolean).join(', ');
    return this.geocodificaConstel(geoConsultaDeEndereco(endereco)).pipe(
      concatMap(coord => (coord ? of(coord) : this.geocodifica(q))),
      map(coord => {
        if (!coord) {
          return this.geocodificaMock(endereco);
        }
        this.apiService.setStorageData('delivery', cacheChave, coord);
        return coord;
      }),
    );
  }

  // Photon, nao Nominatim: o Nominatim nao encontra os enderecos reais das unidades, que
  // estao no OSM. As buscas precisam levar bairro e UF - sem eles o Photon casa nome de rua
  // igual em outro estado e devolve sem sinal de erro.
  private geocodifica(query: string): Observable<Coordenada | null> {
    const url = `https://photon.komoot.io/api/?limit=1&q=${encodeURIComponent(query)}`;
    return this._httpClient.get<any>(url).pipe(
      map(res => {
        const ponto = res?.features?.[0]?.geometry?.coordinates;
        return ponto ? { latitude: ponto[1], longitude: ponto[0] } : null;
      }),
      catchError(() => of(null)),
    );
  }

  // Silencioso como o Photon: falhar aqui so passa a vez para o proximo provedor.
  private geocodificaConstel(consulta: GeoCoordenadasConsulta | null): Observable<Coordenada | null> {
    if (!consulta) {
      return of(null);
    }
    const url = `${this.apiService.getMapping('aps://integracao/geo/coordenadas')}?${geoConsultaParametros(consulta)}`;
    return this._httpClient.get<GeoCoordenadas>(url).pipe(
      map(retorno => geoCoordenada(retorno)),
      catchError(() => of(null)),
    );
  }

  private geocodificaMock(endereco: Endereco): Coordenada {
    const origem = (this.taxaConfig || taxaEntregaConfigPadrao()).origem;
    const digitos = (endereco.cep || '').replace(/\D/g, '');
    const semente = digitos ? parseInt(digitos.slice(-4), 10) : 0;
    const offsetKm = semente % 12;
    const rumo = ((semente % 360) * Math.PI) / 180;
    const dLat = (offsetKm / 111) * Math.cos(rumo);
    const dLng = (offsetKm / (111 * Math.cos((origem.latitude * Math.PI) / 180))) * Math.sin(rumo);
    return { latitude: origem.latitude + dLat, longitude: origem.longitude + dLng };
  }

  // Chave ANTIGA: guardava UM endereco (objeto). Continua sendo lida uma vez, para
  // migrar quem ja tinha endereco salvo, e depois some.
  // Endereco, cartao e historico sao do CLIENTE. Sem o identificador na chave, quem
  // entrasse neste navegador via os dados de quem entrou antes. O balde `anonimo` NAO e
  // adotado por quem loga depois, pelo mesmo motivo.
  private clienteChave(): string {
    return this.clientePedido()?.identificador || 'anonimo';
  }

  private enderecosChave(): string {
    return `enderecos.${this.empresaId}.${this.estabelecimentoId}.${this.clienteChave()}`;
  }

  private enderecoEscolhidoChave(): string {
    return `enderecoEscolhido.${this.empresaId}.${this.estabelecimentoId}.${this.clienteChave()}`;
  }

  // `endereco` continua sendo o endereco EM USO. Checkout, frete e `confirma()` leem so
  // ele e nao precisaram mudar - a lista entra por cima.
  //
  // A agenda tem DUAS origens: o servidor quando ha cliente logado, o `localStorage`
  // quando nao ha. A do servidor manda - e o dado que segue o cliente para outro aparelho.
  enderecoCarrega() {
    if (!this.isDelivery) {
      this.endereco = null;
      this.enderecos = [];
      this.onEnderecoChange.next(this.endereco);
      this.onEnderecosChange.next(this.enderecos);
      return;
    }
    if (this.agenteCliente) {
      this.agenteAgendaAplica();
      return;
    }
    this.enderecos = this.enderecosLe();
    this.enderecoEmUsoDefine();
  }

  // Qual endereco esta em uso e preferencia DO APARELHO: o servidor nao tem esse campo,
  // entao continua no storage mesmo com a agenda vindo de fora.
  private enderecoEmUsoDefine(): void {
    const escolhido = this.apiService.getStorageData('delivery', this.enderecoEscolhidoChave());
    this.endereco =
      this.enderecos.find(e => e.id === escolhido) || this.enderecos[0] || null;
    this.onEnderecosChange.next(this.enderecos);
    this.onEnderecoChange.next(this.endereco);
    this.freteRecalcula();
  }

  // Precisa da lista de UF: o `agente/cliente` manda `uf` sem `sigla`, e sem ela a
  // validacao do front julga INCOMPLETO um endereco que esta inteiro.
  private agenteAgendaAplica(): void {
    this.ufLista().subscribe(ufs => {
      this.enderecos = agenteEnderecosParaAgenda(this.agenteCliente, ufs);
      this.enderecoEmUsoDefine();
    });
  }

  private enderecosLe(): Endereco[] {
    const lista = this.apiService.getStorageData('delivery', this.enderecosChave());
    if (Array.isArray(lista)) {
      return lista.map(dado => Object.assign(new Endereco(), dado));
    }
    // A migracao do formato antigo saiu com o escopo por cliente: aquele registro nao tem
    // dono, e adota-lo daria o endereco de quem cadastrou para quem logar primeiro.
    return [];
  }

  private enderecoEscolhidoGrava(): void {
    this.apiService.setStorageData(
      'delivery',
      this.enderecoEscolhidoChave(),
      this.endereco?.id || '',
    );
  }

  // So para a agenda LOCAL (cliente sem login). Com agenda do servidor a lista nao e
  // espelhada aqui: duas copias divergem, e a do servidor e a verdadeira.
  private enderecosGrava(): void {
    this.apiService.setStorageData('delivery', this.enderecosChave(), this.enderecos);
    this.enderecoEscolhidoGrava();
    this.onEnderecosChange.next(this.enderecos);
    this.onEnderecoChange.next(this.endereco);
  }

  // Cria ou atualiza, pelo id. O endereco salvo passa a ser o em uso - quem acabou de
  // cadastrar quer receber ali.
  //
  // A tela e atualizada ANTES da resposta do servidor, de proposito: a assinatura e void e
  // o formulario fecha na hora, entao esperar deixaria a lista velha na tela.
  enderecoSalva(endereco: Endereco) {
    const novo = !endereco.id;
    if (novo) {
      endereco.id = Date.now().toString();
    }
    const i = this.enderecos.findIndex(e => e.id === endereco.id);
    if (i >= 0) {
      this.enderecos[i] = endereco;
    } else {
      this.enderecos = [...this.enderecos, endereco];
    }
    this.endereco = endereco;
    if (this.agenteCliente) {
      this.onEnderecosChange.next(this.enderecos);
      this.onEnderecoChange.next(this.endereco);
      this.freteRecalcula();
      this.agenteEnderecoGrava(endereco, novo);
      return;
    }
    this.enderecosGrava();
    this.freteRecalcula();
  }

  enderecoSeleciona(id: string): void {
    const achado = this.enderecos.find(e => e.id === id);
    if (!achado) {
      return;
    }
    this.endereco = achado;
    if (this.agenteCliente) {
      this.enderecoEscolhidoGrava();
      this.onEnderecoChange.next(this.endereco);
      this.freteRecalcula();
      return;
    }
    this.enderecosGrava();
    this.freteRecalcula();
  }

  // Apagar o endereco em uso escolhe outro no lugar: ficar sem endereco no meio do
  // checkout trava o pedido de entrega.
  enderecoRemove(id: string): void {
    const doServidor = !!this.agenteCliente;
    this.enderecos = this.enderecos.filter(e => e.id !== id);
    if (this.endereco?.id === id) {
      this.endereco = this.enderecos[0] || null;
    }
    if (doServidor) {
      this.onEnderecosChange.next(this.enderecos);
      this.onEnderecoChange.next(this.endereco);
      this.freteRecalcula();
      this.agenteEnderecoExclui(id);
      return;
    }
    this.enderecosGrava();
    this.freteRecalcula();
  }

  enderecoResumo(endereco: Endereco): string {
    if (!endereco) {
      return '';
    }
    const complemento = endereco.complemento ? ` - ${endereco.complemento}` : '';
    return `${endereco.logradouro}, ${endereco.numero}${complemento} - ${endereco.bairro} - ${endereco.cidade}/${endereco.uf}`;
  }

  // CEP pelo backend, no lugar do ViaCEP. O `cep` do retorno e ignorado de proposito:
  // vem com ponto, e quem manda no campo e o que o cliente digitou.
  enderecoBuscaCep(cep: string): Observable<CepResultado> {
    const cepNorm = cepDigitos(cep);
    if (cepNorm.length !== 8) {
      return of({ situacao: 'indisponivel' } as CepResultado);
    }
    // `_httpClient` pelo mesmo motivo do panorama: a tela ja avisa com as proprias palavras.
    const url = this.apiService.getMapping(`aps://integracao/cep/${cepNorm}`);
    return this._httpClient.get<CepConsulta>(url).pipe(
      map((res): CepResultado => {
        if (!res) {
          return { situacao: 'indisponivel' };
        }
        if (res.valido === false) {
          return { situacao: 'inexistente' };
        }
        return {
          situacao: 'encontrado',
          endereco: {
            logradouro: res.logradouro || '',
            bairro: res.bairro || '',
            cidade: res.municipio?.nome || '',
            uf: res.municipio?.uf?.sigla || '',
            ufCodigo: res.municipio?.uf?.codigo || '',
            municipioCodigo: res.municipio?.codigo || '',
          },
        };
      }),
      // Falha de rede nao vira "inexistente": travaria o cadastro por algo que nao e do cliente.
      catchError(() => of({ situacao: 'indisponivel' } as CepResultado)),
    );
  }

  // ---------- UF e municipio ----------

  // Em memoria depois da primeira busca: o formulario abre e fecha varias vezes num pedido.
  ufLista(): Observable<Uf[]> {
    if (this.ufs) {
      return of(this.ufs);
    }
    return this._httpClient.get<Uf[]>(this.apiService.getMapping('aps://uniao/uf')).pipe(
      map(ufs => {
        this.ufs = ufListaEntrega(Array.isArray(ufs) ? ufs : []);
        return this.ufs;
      }),
      catchError(() => of([])),
    );
  }

  // Filtra pelo codigo IBGE da UF, nao pela sigla.
  municipioLista(ufCodigo: string): Observable<Municipio[]> {
    if (!ufCodigo) {
      return of([]);
    }
    if (this.municipios[ufCodigo]) {
      return of(this.municipios[ufCodigo]);
    }
    const url = this.apiService.getMapping('aps://uniao/municipio');
    return this._httpClient.get<Municipio[]>(`${url}?ufcodigo=${ufCodigo}`).pipe(
      map(lista => {
        this.municipios[ufCodigo] = Array.isArray(lista) ? lista : [];
        return this.municipios[ufCodigo];
      }),
      catchError(() => of([])),
    );
  }

  // ---------- localizacao do aparelho ----------

  // So pelo botao "Usar minha localizacao": pedir permissao de GPS sem o cliente ter
  // pedido espanta, e o navegador penaliza o site.
  localizacaoAtual(): Observable<LocalizacaoEndereco | null> {
    if (!navigator?.geolocation) {
      return of(null);
    }
    return new Observable<Coordenada | null>(observador => {
      navigator.geolocation.getCurrentPosition(
        posicao => {
          observador.next({ latitude: posicao.coords.latitude, longitude: posicao.coords.longitude });
          observador.complete();
        },
        () => {
          observador.next(null);
          observador.complete();
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
      );
    }).pipe(
      concatMap(coord => {
        if (!coord) {
          return of(null);
        }
        return this._httpClient
          .get<any>(`https://photon.komoot.io/reverse?lat=${coord.latitude}&lon=${coord.longitude}`)
          .pipe(
            map(resposta => localizacaoDePhoton(resposta, coord)),
            catchError(() => of(null)),
          );
      }),
    );
  }

  getCardapio(): Promise<{ ok: boolean }> {
    if (!(this.empresaId && this.estabelecimentoId)) {
      return Promise.resolve({ ok: false });
    }
    return new Promise((resolve, reject) => {
      this.cardapio = {
        categorias: [],
      };
      this.apiService.encontra<Cardapio>(`aps://integracao/cardapio/menu/${this.empresaId}/${this.estabelecimentoId}`, '').subscribe((cardapio: Cardapio) => {
        const products: EComProduct[] = [];
        // Os destaques viram carrossel a partir do `item.destaque`; a categoria de mesmo
        // nome que o backend manda repetiria os itens logo abaixo.
        cardapio.categorias = cardapioSemVazias(cardapioSemDestaques(cardapio)).categorias;
        cardapio.categorias.forEach(categoria => {
          categoria.imagem ??= 'assets/images/ecommerce/categoria.png';
          categoria.itens.forEach(item => {
            // if (['a', 'b', 'c', '1', '2', '3'].includes(item.id.substring(2, 2).toLowerCase())) {
            //   item['valorOriginal'] = 3;
            // }
            item.imagem ??= 'assets/images/ecommerce/item.png';
            products.push({
              id: item.id,
              name: item.nome,
              slug: 'memo',
              brand: 'Geral',
              price: item.valor,
              image: item.imagem,
              hasFreeShipping: true,
              rating: 5,
              description: item.descricao,
            });
          });
        });
        this.cardapio = cardapio;
        this.productList = products;
        this.onCardapioChange.next(cardapio);
        this.onProductListChange.next(this.productList);
        resolve({ ok: true });
      }, reject);
    });
  }

  getItemDetalhado(id: string): Observable<Item> {
    return this.apiService.obtem<Item>(`aps://integracao/cardapio/item`, id);
  }

  /**
   * Get Wishlist
   */
  getWishlist(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this._httpClient.get('api/ecommerce-userWishlist').subscribe((response: any) => {
        this.wishlist = response;
        this.onWishlistChange.next(this.wishlist);
        resolve(this.wishlist);
      }, reject);
    });
  }

  /**
   * Get CartList
   */
  getCartList(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this._httpClient.get('api/ecommerce-userCart').subscribe((response: any) => {
        this.cartList = response;
        this.onCartListChange.next(this.cartList);
        resolve(this.cartList);
      }, reject);
    });
  }

  /**
   * Get Selected Product
   */
  getSelectedProduct(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const product = this.productList?.filter(product => product.id === this.idHandel);
      this.selectedProduct = product;
      this.onSelectedProductChange.next(product);
      resolve(this.selectedProduct);
    });
  }

  getProduct(): EComProduct {
    return this.productList.find(product => product.id === this.idHandel);
  }

  /**
   * Get Related Products
   */
  getRelatedProducts(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this._httpClient.get('api/ecommerce-relatedProducts').subscribe((response: any) => {
        this.relatedProducts = response;
        this.onRelatedProductsChange.next(this.relatedProducts);
        resolve(this.relatedProducts);
      }, reject);
    });
  }

  /**
   * Sort Product
   *
   * @param sortBy
   */
  sortProduct(sortBy) {
    let sortDesc = false;

    const sortByKey = (() => {
      if (sortBy === 'price-desc') {
        sortDesc = true;
        return 'price';
      }
      if (sortBy === 'price-asc') {
        return 'price';
      }
      sortDesc = true;
      return 'id';
    })();

    const sortedData = this.productList.sort(this.sortRef(sortByKey));
    if (sortDesc) sortedData.reverse();
    this.productList = sortedData;
    this.onProductListChange.next(this.productList);
  }

  /**
   * Add In Wishlist
   *
   * @param id
   */
  addToWishlist(id) {
    return new Promise<void>((resolve, reject) => {
      const lengthRef = this.wishlist.length + 1;
      const wishRef = { id: lengthRef, productId: id };

      this._httpClient.post('api/ecommerce-userWishlist/' + lengthRef, { ...wishRef }).subscribe(response => {
        this.getWishlist();
        resolve();
      }, reject);
    });
  }

  /**
   * Remove From Wishlist
   *
   * @param id
   */
  removeFromWishlist(id) {
    const indexRef = this.wishlist.findIndex(wishlistRef => wishlistRef.productId === id); // Get the index ref
    const indexId = this.wishlist[indexRef].id; // Get the product wishlist id from indexRef
    return new Promise<void>((resolve, reject) => {
      this._httpClient.delete('api/ecommerce-userWishlist/' + indexId).subscribe((response: any) => {
        this.getWishlist();
        resolve();
      }, reject);
    });
  }

  getLinha(_id: string) {
    for (const linha of this.sacola.linhas) {
      if (linha._id === _id) {
        return linha;
      }
    }
    console.error('Item not found');
    return null;
  }

  getItem(id: string) {
    for (const categoria of this.cardapio.categorias) {
      for (const item of categoria.itens) {
        if (item.id === id) {
          return item;
        }
      }
    }
    console.error('Item not found');
    return null;
  }

  /**
   * Add In Cart
   *
   * @param id
   */
  addToCart(id: string): Promise<void> {
    const item = this.getItem(id);
    if (!item) {
      return;
    }
    this.sacola.adiciona(item);
    return new Promise<void>((resolve, reject) => {
      const product = this.productList.find(product => product.id === id);
      if (product) {
        product.isInCart = true;
      }
      const maxValueId = Math.max(...this.cartList.map(cart => cart.id), 0) + 1;
      const cartRef = { id: maxValueId, productId: id, qty: 1 };
      var cartId: any = '';

      // If cart is not Empty
      if (maxValueId !== 1) {
        cartId = maxValueId;
      }
      this.onSacolaChange.next(this.sacola);
      this._httpClient.post('api/ecommerce-userCart/' + cartId, { ...cartRef }).subscribe(response => {
        this.getCartList();
        resolve();
      }, reject);
    });
  }

  /**
   * Add In Cart
   *
   * @param id
   */
  addComposicaoToCart(item: Item): Promise<void> {
    // const item = this.getItem(id);
    if (!item) {
      return;
    }
    this.sacola.adiciona(item, item.quantidade);
    return new Promise<void>((resolve, reject) => {
      const product = this.productList.find(product => product.id === item.id);
      if (product) {
        product.isInCart = true;
      }
      const maxValueId = Math.max(...this.cartList.map(cart => cart.id), 0) + 1;
      const cartRef = { id: maxValueId, productId: item.id, qty: item.quantidade };
      var cartId: any = '';

      // If cart is not Empty
      if (maxValueId !== 1) {
        cartId = maxValueId;
      }
      this.onSacolaChange.next(this.sacola);
      this._httpClient.post('api/ecommerce-userCart/' + cartId, { ...cartRef }).subscribe(response => {
        this.getCartList();
        resolve();
      }, reject);
    });
  }

  sacolaIdentifica(cliente: SacolaCliente) {
    this.sacola.identifica(cliente);
  }

  sacolaLinhaQuantidade(linha: SacolaLinha, quantidade: number) {
    const _linha = this.getLinha(linha._id);
    if (!_linha) {
      return;
    }
    // Nao gravar a quantidade aqui: a sacola precisa da antiga para escalar as sublinhas
    // da montagem, e ela mesma grava a nova.
    this.sacola.linhaAtualizaQuantidade(_linha, quantidade);
    this.onSacolaChange.next(this.sacola);
  }

  // Edicao de item SIMPLES pela sacola: troca observacao e quantidade na propria linha.
  // Readicionar e apagar a antiga, como a montagem faz, nao serve aqui: com a mesma
  // observacao a sacola somaria na linha antiga, e apagar a antiga levaria tudo.
  sacolaLinhaEdita(linha: SacolaLinha, quantidade: number, observacoes: string) {
    const _linha = this.getLinha(linha._id);
    if (!_linha) {
      return;
    }
    _linha.item = { ..._linha.item, observacoes: (observacoes || "").trim() };
    this.sacolaLinhaQuantidade(_linha, quantidade);
  }

  /**
   * Remove From Cart
   *
   * @param id
   */
  removeFromCart(linha: SacolaLinha) {
    this.sacola.remove(linha);
    this.onSacolaChange.next(this.sacola);
    // `cartList` e o carrinho de demonstracao do template. Item que nao esta nele (o que
    // entra pela janela da vitrine, por exemplo) quebrava aqui antes de avisar a tela, e a
    // sacola parecia nao remover.
    const indexRef = this.cartList.findIndex(cartListRef => cartListRef.productId === linha.item.id); // Get the index ref
    if (indexRef < 0) {
      return Promise.resolve();
    }
    const indexId = this.cartList[indexRef].id; // Get the product wishlist id from indexRef
    return new Promise<void>((resolve, reject) => {
      this._httpClient.delete('api/ecommerce-userCart/' + indexId).subscribe((response: any) => {
        this.getCartList();
        resolve();
      }, reject);
    });
  }

  // Devolve o resultado em vez de navegar sozinho: quem chama e que decide o que
  // mostrar. Antes o sucesso era so um toast que sumia em 2,5s e voltava ao cardapio.
  confirma(opcoes: {
    entregaTipo?: number;
    formaPagamento?: { forma: number; nome: string };
    frete?: number;
    trocoPara?: number;
    voucher?: Voucher;
  } = {}): Observable<boolean> {
    const pedido = new Pedido();
    pedido.tipo = PedidoTipo.Autoatendimento;
    pedido.empresa = {
      id: this.empresa.id,
      codigo: this.empresa.codigo,
      nome: this.empresa.nome,
    };
    pedido.estabelecimento = {
      id: this.estabelecimento.id,
      codigo: this.estabelecimento.codigo,
      nome: this.estabelecimento.nome,
    };
    pedido.referencia = '';
    if (this.localizador) {
      pedido.tipo = this.localizador.tipo || PedidoTipo.Autoatendimento;
      pedido.localizador = {
        id: this.localizador.id,
        codigo: this.localizador.codigo,
        nome: this.localizador.nome,
        tipo: this.localizador.tipo,
      };
      pedido.referencia = this.localizador.codigo;
    }
    const entregaTipo = opcoes.entregaTipo || EntregaTipo.Entrega;
    // O `tipo` nasce Autoatendimento (que a loja trata como RETIRADA). Antes a condicao
    // de entrega exigia `&& this.endereco` e, sem endereco, NENHUM dos dois ramos rodava:
    // o pedido era gravado como retirada em silencio. O cliente pedia entrega e o pedido
    // chegava na loja como retirada, sem frete e sem endereco, e ninguem era avisado.
    // Agora falta de endereco na entrega para o pedido em vez de trocar o tipo por baixo.
    if (this.isDelivery && entregaTipo === EntregaTipo.Entrega) {
      if (!this.endereco) {
        this.apiService.exibeErro('Informe o endereço de entrega antes de finalizar o pedido.');
        return of(false);
      }
      pedido.tipo = PedidoTipo.Delivery;
      pedido.pedidoEntrega = {
        cep: cepDigitos(this.endereco.cep),
        logradouro: this.endereco.logradouro,
        numero: agenteNumeroServidor(this.endereco.numero) ?? 0,
        local: ENTREGA_LOCAL_DESTINATARIO,
        bairro: this.endereco.bairro,
        municipio: enderecoMunicipioEntidade(this.endereco),
        uf: enderecoUfEntidade(this.endereco),
        responsavel: ENTREGA_RESPONSAVEL,
      };
      const complemento = (this.endereco.complemento || '').trim();
      if (complemento) {
        pedido.pedidoEntrega.complemento = complemento;
      }
      // `referencia` aceita no maximo 20 caracteres - o resumo do endereco nao cabe.
      pedido.referencia = 'Entrega';
    } else if (this.isDelivery && entregaTipo === EntregaTipo.Retirada) {
      pedido.tipo = PedidoTipo.Autoatendimento;
      pedido.referencia = 'Retirada';
    }
    if (opcoes.formaPagamento) {
      pedido.pedidoPagamentos = [{
        sequencial: 1,
        formaIdentificador: opcoes.formaPagamento.forma,
        formaNome: opcoes.formaPagamento.nome,
        total: 0,
        trocoPara: opcoes.trocoPara > 0 ? opcoes.trocoPara : 0,
        pago: false,
      }];
    }
    const cliente = this.clientePedido();
    if (cliente) {
      pedido.pedidoCliente = Object.assign(new Cliente(), cliente);
      pedido.cliente = Object.assign(new Cliente(), cliente);
    }
    // O `cliente.id` e o que amarra o pedido ao cadastro: o historico consulta por
    // `planilha?clienteid={id}`, e sem ele o backend guarda apenas nome e imagem.
    if (this.agenteCliente?.id) {
      pedido.cliente = Object.assign(new Cliente(), {
        id: this.agenteCliente.id,
        codigo: this.agenteCliente.codigo || '',
        nome: this.agenteCliente.nome || cliente?.nome || '',
      });
    }
    pedido.modelo = PedidoModelo.Constel;
    pedido.pedidoItens = [];
    var sequencial = 0;
    this.sacola.linhas.forEach(linha => {
      const pedidoItem = new PedidoItem();
      pedidoItem.item = new Item();
      pedidoItem.sequencial = ++sequencial;
      pedidoItem.item.id = linha.item.id;
      pedidoItem.item.nome = linha.item.id;
      pedidoItem.itemIdentificador = linha.item.id;
      pedidoItem.itemNome = linha.item.nome;
      pedidoItem.valor = linha.valor;
      pedidoItem.quantidade = linha.quantidade;
      pedidoItem.medida = 'UN';
      pedidoItem.subtotal = linha.total;
      pedidoItem.opcionais = 0.00;
      pedidoItem.total = linha.total;
      if (linha.item.observacoes?.trim()) {
        pedidoItem.observacoes = linha.item.observacoes.trim();
      }
      pedido.pedidoItens.push(pedidoItem);
    });
    const frete = pedido.tipo === PedidoTipo.Delivery ? (opcoes.frete ?? this.getFreteEntrega()) : 0.00;
    pedido.subtotal = this.sacola.total;
    pedido.acrescimo = 0.00;
    pedido.frete = frete;
    pedido.abatimento = 0.00;
    // O cupom incide so sobre o subtotal - o frete fica de fora. Se deve ou nao abater
    // a entrega e regra de negocio ainda em aberto (ver `docs/HANDOFF-BACKEND-cupom.md`).
    // ATENCAO: este valor vai calculado do NAVEGADOR e o `pedido/grava` aceita o que
    // mandamos. Antes de producao o servidor precisa revalidar o cupom e recalcular,
    // senao da para forjar desconto editando a requisicao.
    pedido.desconto = opcoes.voucher
      ? opcoes.voucher.descontoCalcula(this.sacola.total)
      : 0.00;
    pedido.total = this.sacola.total + frete - pedido.desconto;
    if (pedido.pedidoPagamentos?.length) {
      pedido.pedidoPagamentos[0].total = pedido.total;
    }
    // const clienteInfo = pedido.pedidoCliente
    //   ? `${pedido.pedidoCliente.nome} (${pedido.pedidoCliente.email})`
    //   : 'Anônimo';
    // alert(`Confirmando pedido de ${clienteInfo}\n${pedido.pedidoItens.length} item(ns) — Total: R$ ${pedido.total.toFixed(2)}`);
    // Pelo `_httpClient`, e nao pelo `apiService.grava`: aquele dispara o toast tecnico de
    // 24s ("A requisicao falhou devido ao seguinte motivo...") antes de chegar aqui, e a
    // recusa de mesa bloqueada tem de aparecer com a frase do PDV, sozinha.
    const url = this.apiService.getMapping(`aps://integracao/pedido/grava`);
    return this._httpClient.post<Pedido>(url, pedido, {
      headers: {
        'empresa-id': this.empresa.id,
        'empresa-nome': this.empresa.nome,
        'estabelecimento-id': this.estabelecimento.id,
        'estabelecimento-nome': this.estabelecimento.nome,
      },
    }).pipe(
      map((gravado: Pedido) => {
        // O `grava` devolve o pedido salvo. Guardamos o id e o codigo dele porque sao a
        // chave para consultar a situacao real depois, em GET integracao/pedido/{id}.
        const resumo = this.pedidoResumoMonta(pedido, opcoes.voucher);
        if (gravado?.id) {
          resumo.id = gravado.id;
          resumo.codigo = (gravado as any).codigo || '';
        }
        this.pedidoEnviadoId = gravado?.id || null;
        // Guarda o resumo ANTES de zerar a sacola: depois nao ha mais o que resumir.
        this.pedidoHistoricoGrava(resumo);
        this.sacola.inicia();
        this.onSacolaChange.next(this.sacola);
        return true;
      }),
      // O ErrorInterceptor ja reduz o erro ao `message` do backend. Texto vira a mensagem
      // (ex.: 'O atendimento "MS 15" esta bloqueado'); lista de validacao ou falha de rede
      // ficam na mensagem generica.
      catchError(erro => {
        // 'Unknown Error' e o statusText do Angular quando nem houve resposta.
        const mensagem = typeof erro === 'string' && erro.trim() !== 'Unknown Error' ? erro.trim() : '';
        this.apiService.exibeErro(mensagem || 'Nao foi possivel enviar o pedido. Tente de novo.');
        return of(false);
      }),
    );
  }

  voltarParaCardapio() {
    const rota = cardapioRota(this.empresa?.id, this.estabelecimento?.id, this.localizador?.id, this.slug);
    if (!rota) {
      return;
    }
    this._router.navigate(rota);
  }

  // A tela de confirmacao usa isto para mostrar exatamente o cliente que vai no pedido.
  clientePedido(): ClientePedido | null {
    return clientePedidoMonta(this.authService.currentUserValue);
  }

  // ---------- cliente no servidor (agente/cliente) ----------

  // Garante que o cliente logado existe no backend: le por `G{sub}` e, se der 404, cria.
  // E o fluxo definido pelo backend (GET; se nao existir, POST). Guarda o cliente em
  // `agenteCliente` e emite `onAgenteClienteChange`, para quem quiser a agenda do servidor.
  //
  // Silencioso: roda no login, entao usa `_httpClient` direto (o `apiService` dispararia
  // toast de erro de 24s) e nunca interrompe o fluxo - falhar aqui nao pode travar a loja.
  agenteClienteGarante(): void {
    const cliente = this.clientePedido();
    if (!cliente?.identificador) {
      this.agenteGarantido = null;
      this.agenteCliente = null;
      return;
    }
    // `resolve()` e a reacao ao login chamam os dois; sem esta trava um cliente que ainda
    // nao existe levaria dois POST.
    if (this.agenteGarantido === cliente.identificador) {
      return;
    }
    this.agenteGarantido = cliente.identificador;
    // Fluxo do backend: GET por identificador; se 404, POST para criar; e entao GET de
    // novo. O refresh nao e redundante - o POST devolve o cliente SEM os arrays da agenda
    // (`clienteEnderecos`/`clienteCartoes`), e o GET os traz. So refaz o GET quando criou.
    this.agenteClienteObtem(cliente.identificador)
      .pipe(
        concatMap(achado => (achado
          ? of(achado)
          : this.agenteClienteCria(cliente).pipe(
              concatMap(criado => (criado ? this.agenteClienteObtem(cliente.identificador) : of(null))),
            ))),
      )
      .subscribe(agente => {
        this.agenteCliente = agente;
        this.onAgenteClienteChange.next(agente);
        // So pode entrar depois que o cliente chega; ate aqui a tela mostra a local.
        if (agente && this.isDelivery) {
          this.agenteAgendaAplica();
        }
      });
  }

  // ---------- alergenos do cliente no servidor ----------

  // Alergenos do cadastro do cliente (`agente/cliente`). Deslogado, perfil vazio.
  get clienteAlergenos(): ClienteAlergenoPerfil {
    return clienteAlergenoPerfil(this.agenteCliente);
  }

  // Nome, identificador, e-mail e telefone para gravar. O perfil usa para saber o que
  // falta pedir: o POST cobra e-mail E telefone, e cada login so traz um dos dois.
  get clienteContato(): AgenteClienteContato | null {
    return agenteClienteContato(this.agenteCliente, this.clientePedido());
  }

  // Grava o perfil de alergenos pelo POST, que cria ou atualiza pelo identificador - nao ha
  // PUT no `agente/cliente`. Como cria tambem, salvar aqui cadastra o cliente que o login
  // nao conseguiu criar (por faltar e-mail ou telefone). Devolve se gravou; quem chama mostra
  // o erro, porque aqui o cliente pediu para salvar e precisa saber.
  agenteClienteAlergenoGrava(perfil: ClienteAlergenoPerfil, contato: AgenteClienteContato): Observable<boolean> {
    const url = this.apiService.getMapping('aps://agente/cliente');
    return this._httpClient.post<AgenteCliente>(url, agenteClienteAlergenoCorpo(contato, perfil)).pipe(
      map(gravado => {
        if (!gravado?.id) {
          return false;
        }
        // A vitrine reage ja; o reler traz a agenda, que o POST nao devolve.
        this.agenteCliente = { ...(this.agenteCliente || {}), ...gravado } as AgenteCliente;
        this.agenteGarantido = gravado.identificador;
        this.onAgenteClienteChange.next(this.agenteCliente);
        this.agenteClienteRele();
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  // ---------- agenda de enderecos no servidor ----------

  private agenteEnderecoUrl(enderecoId?: string): string {
    const base = `aps://agente/cliente/${this.agenteCliente.id}/endereco`;
    return this.apiService.getMapping(enderecoId ? `${base}/${enderecoId}` : base);
  }

  // Rele o cliente inteiro depois de gravar: e a unica forma de saber o `id` que o
  // servidor deu ao endereco recem-criado.
  private agenteEnderecoGrava(endereco: Endereco, novo: boolean): void {
    const corpo = enderecoParaAgente(endereco);
    const chamada = novo
      ? this._httpClient.post(this.agenteEnderecoUrl(), corpo)
      : this._httpClient.put(this.agenteEnderecoUrl(endereco.id), corpo);
    chamada.pipe(catchError(() => of(null))).subscribe(res => {
      if (!res) {
        // Nao desfaz o que esta na tela: o endereco otimista continua valendo para ESTE
        // pedido, porque o `confirma()` le `this.endereco` e nao o servidor. O que se
        // perde e a persistencia, e o cliente precisa saber - some ao trocar de aparelho.
        this.apiService.exibeErro(
          'Nao foi possivel salvar o endereco na sua conta. Ele vale para este pedido, mas pode nao aparecer em outro aparelho.',
        );
        return;
      }
      this.agenteClienteRele();
    });
  }

  private agenteEnderecoExclui(enderecoId: string): void {
    this._httpClient
      .delete(this.agenteEnderecoUrl(enderecoId), { observe: 'response' })
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (!res) {
          this.apiService.exibeErro('Nao foi possivel remover o endereco da sua conta.');
        }
        this.agenteClienteRele();
      });
  }

  // Depois de qualquer escrita, quem manda e o servidor: a agenda e relida de la.
  private agenteClienteRele(): void {
    const identificador = this.agenteCliente?.identificador || this.clientePedido()?.identificador;
    if (!identificador) {
      return;
    }
    this.agenteClienteObtem(identificador).subscribe(agente => {
      if (!agente) {
        return;
      }
      this.agenteCliente = agente;
      this.onAgenteClienteChange.next(agente);
      if (this.isDelivery) {
        this.agenteAgendaAplica();
      }
    });
  }

  private agenteClienteObtem(identificador: string): Observable<AgenteCliente | null> {
    const url = this.apiService.getMapping(`aps://agente/cliente/${identificador}`);
    return this._httpClient.get<AgenteCliente>(url).pipe(
      catchError(() => of(null)),
    );
  }

  private agenteClienteCria(cliente: ClientePedido): Observable<AgenteCliente | null> {
    // `alergenos` virou obrigatorio no POST (2026-10-28); cliente novo nasce sem perfil.
    const novo: AgenteClienteNovo = {
      nome: cliente.nome,
      identificador: cliente.identificador,
      alergenos: false,
    };
    // O backend exige URL com protocolo; a imagem padrao (`avatar-s-11.jpg`) do login por
    // telefone daria 400. A foto do Google ja vem como URL.
    if (/^https?:\/\//.test(cliente.imagem || '')) {
      novo.imagem = cliente.imagem;
    }
    // Vazio o backend recusa; ausente ele aceita quando ha telefone (CON-3683).
    if (cliente.email) {
      novo.email = cliente.email;
    }
    if (cliente.telefone) {
      novo.telefone = cliente.telefone;
    }
    const url = this.apiService.getMapping('aps://agente/cliente');
    return this._httpClient.post<AgenteCliente>(url, novo).pipe(
      catchError(() => of(null)),
    );
  }

  private pedidosChave(): string {
    return `pedidos.${this.empresaId}.${this.estabelecimentoId}.${this.clienteChave()}`;
  }

  private pedidoHistoricoLocal(): PedidoResumo[] {
    const dado = this.apiService.getStorageData('pedidos', this.pedidosChave());
    return Array.isArray(dado) ? (dado as PedidoResumo[]) : [];
  }

  // Historico do servidor por cliente. Deslogado nao ha `clienteid`, e vale so o aparelho.
  // Silencioso: queda de rede cai no historico local, sem toast.
  pedidoHistoricoLista(): Observable<PedidoResumo[]> {
    const local = this.pedidoHistoricoLocal();
    const clienteId = this.agenteCliente?.id;
    if (!clienteId) {
      return of(historicoUne([], local));
    }
    const url = this.apiService.getMapping(pedidoPlanilhaConsulta(clienteId));
    return this._httpClient.get<PedidoPlanilha>(url).pipe(
      map(retorno => historicoUne(
        pedidoPlanilhaLista(retorno).map(linha => this.pedidoResumoSituacaoAplica(pedidoPlanilhaParaResumo(linha))),
        local,
      )),
      catchError(() => of(historicoUne([], local))),
    );
  }

  // A situacao avancada na tela de aceite vale mais que a do backend, igual no resto.
  private pedidoResumoSituacaoAplica(resumo: PedidoResumo): PedidoResumo {
    const local = this.situacoesLocais()[resumo.id];
    return local ? { ...resumo, situacao: local } : resumo;
  }

  // A situacao vem do backend, nao de mock: GET integracao/pedido/{id} devolve o pedido
  // inteiro, com `situacao` no mesmo enum que o front ja declara.
  pedidoSituacaoObtem(id: string): Observable<PedidoSituacao | null> {
    // Pedido gravado antes de guardarmos o id real tem chave local, que o backend nao conhece.
    if (!id || !/^[0-9a-f-]{36}$/i.test(id)) {
      return of(null);
    }
    // Os mesmos cabecalhos do `grava`. Sem eles o backend responde "Empresa nao
    // identificada nas credenciais" e o ErrorInterceptor joga o cliente para fora.
    if (!this.empresa?.id || !this.estabelecimento?.id) {
      return of(null);
    }
    // Situacao avancada na tela de aceite vence a do backend. E o que permite testar o
    // acompanhamento ponta a ponta enquanto nao existe endpoint de transicao.
    const local = this.situacoesLocais()[id];
    if (local) {
      return of(local);
    }
    return this.apiService.obtem<Pedido>('aps://integracao/pedido', id, '', {
      'empresa-id': this.empresa.id,
      'empresa-nome': this.empresa.nome,
      'estabelecimento-id': this.estabelecimento.id,
      'estabelecimento-nome': this.estabelecimento.nome,
    }).pipe(
      map(pedido => (pedido?.situacao ?? null)),
      catchError(() => of(null)),
    );
  }

  // Situacao dos pedidos do APARELHO feitos nas ultimas `horas` - os do servidor ja vem com
  // ela na listagem. So os recentes sao perguntados: so eles podem estar em andamento, e
  // cada um custa uma requisicao. Usado pela barra do perfil e pela faixa da vitrine.
  pedidoSituacoesRecentes(pedidos: PedidoResumo[], horas: number): Observable<{ [id: string]: PedidoSituacao }> {
    const limite = Date.now() - horas * 60 * 60 * 1000;
    const faltam = (pedidos || []).filter(pedido => !pedido.situacao && Date.parse(pedido.criadoEm || '') >= limite);
    if (!faltam.length) {
      return of({});
    }
    return forkJoin(faltam.map(pedido => this.pedidoSituacaoObtem(pedido.id).pipe(first()))).pipe(
      map(situacoes => faltam.reduce(
        (todas, pedido, i) => (situacoes[i] !== null ? { ...todas, [pedido.id]: situacoes[i] } : todas),
        {} as { [id: string]: PedidoSituacao },
      )),
    );
  }

  // ---------- aceite de pedido (tela do lojista) ----------
  //
  // A LEITURA e real: `GET aps://integracao/pedido?situacao={n}` lista os pedidos.
  // O parametro `situacao` e OBRIGATORIO - sem ele o retorno e 400.
  //
  // A ESCRITA nao existe: nenhum verbo de situacao responde no `aps://` (todos 404),
  // e o `pedido/grava` com id troca para validacao de entidade inteira. Por isso o
  // avanco de situacao fica gravado aqui no navegador, por cima do que o backend diz.
  // E MOCK, e so serve para exercitar a tela de acompanhamento do cliente sem depender
  // da retaguarda. Quando existir o endpoint, so `pedidoSituacaoDefine` muda.

  private situacoesChave(): string {
    return `situacao.${this.empresaId}.${this.estabelecimentoId}`;
  }

  private situacoesLocais(): { [id: string]: PedidoSituacao } {
    const dado = this.apiService.getStorageData('situacao', this.situacoesChave());
    return dado && typeof dado === 'object' ? (dado as { [id: string]: PedidoSituacao }) : {};
  }

  // O codigo vem como string com zeros a esquerda ("0010251"). Comparar como numero
  // evita surpresa quando o numero de digitos mudar.
  // Pedidos escondidos do quadro. E SO VISUAL: some da tela de quem escondeu e nao
  // muda nada no backend nem no acompanhamento do cliente. Serve para tirar da frente
  // a montanha de pedido antigo de teste sem inventar situacao que eles nao tem.
  private ocultosChave(): string {
    return `ocultos.${this.empresaId}.${this.estabelecimentoId}`;
  }

  private ocultosLista(): string[] {
    const dado = this.apiService.getStorageData('situacao', this.ocultosChave());
    return Array.isArray(dado) ? (dado as string[]) : [];
  }

  pedidoOculta(ids: string[]): Observable<boolean> {
    const atuais = new Set(this.ocultosLista());
    ids.forEach(id => atuais.add(id));
    this.apiService.setStorageData('situacao', this.ocultosChave(), Array.from(atuais));
    return of(true);
  }

  private pedidoCodigoNumero(pedido: Pedido): number {
    const numero = parseInt((pedido?.codigo || '').replace(/\D/g, ''), 10);
    return isNaN(numero) ? 0 : numero;
  }

  private pedidoCabecalhos(): Record<string, string> | null {
    if (!this.empresa?.id || !this.estabelecimento?.id) {
      return null;
    }
    return {
      'empresa-id': this.empresa.id,
      'empresa-nome': this.empresa.nome,
      'estabelecimento-id': this.estabelecimento.id,
      'estabelecimento-nome': this.estabelecimento.nome,
    };
  }

  private pedidoSituacaoBusca(situacao: PedidoSituacao): Observable<Pedido[]> {
    const cabecalhos = this.pedidoCabecalhos();
    if (!cabecalhos) {
      return of([]);
    }
    return this.apiService
      .encontra<Pedido[]>('aps://integracao/pedido', `situacao=${situacao}`, cabecalhos)
      .pipe(
        map(pedidos => (Array.isArray(pedidos) ? pedidos : [])),
        catchError(() => of([])),
      );
  }

  // Monta o quadro inteiro de uma vez, e NAO uma coluna por vez. A diferenca importa:
  // um pedido avancado localmente continua vindo do backend na situacao ANTIGA, entao
  // consultar coluna a coluna faria ele sumir da origem sem aparecer no destino.
  // Aqui as listas sao unidas, o override e aplicado e so entao o pedido e distribuido
  // pela situacao EFETIVA - assim ele anda de coluna de verdade.
  pedidoQuadroCarrega(situacoes: PedidoSituacao[]): Observable<{ [situacao: number]: Pedido[] }> {
    if (!situacoes.length) {
      return of({});
    }
    const locais = this.situacoesLocais();
    const ocultos = new Set(this.ocultosLista());
    return forkJoin(situacoes.map(situacao => this.pedidoSituacaoBusca(situacao))).pipe(
      map(listas => {
        const vistos = new Set<string>(ocultos);
        const quadro: { [situacao: number]: Pedido[] } = {};
        situacoes.forEach(situacao => (quadro[situacao] = []));

        listas.forEach(lista =>
          lista.forEach(pedido => {
            if (vistos.has(pedido.id)) {
              return;
            }
            vistos.add(pedido.id);
            const local = locais[pedido.id];
            const efetiva = local || pedido.situacao;
            if (quadro[efetiva]) {
              quadro[efetiva].push(local ? ({ ...pedido, situacao: efetiva } as Pedido) : pedido);
            }
            // Situacao fora do quadro (recusado, entregue) simplesmente nao aparece.
          }),
        );

        // Mais recente primeiro. A lista NAO traz data (so id/codigo/situacao/tipo), mas o
        // `codigo` e sequencial por loja, entao serve de indicador de recencia. Buscar a
        // data real custaria uma requisicao por pedido.
        situacoes.forEach(situacao =>
          quadro[situacao].sort((a, b) => this.pedidoCodigoNumero(b) - this.pedidoCodigoNumero(a)),
        );
        return quadro;
      }),
      catchError(() => of({})),
    );
  }

  // Pedido inteiro: cliente, itens, pagamento, entrega e totais. A LISTA nao traz nada
  // disso (so id/codigo/situacao/tipo), entao o detalhe e uma requisicao a parte - por
  // isso a tela busca sob demanda, ao abrir o pedido, e nao para o quadro todo.
  pedidoDetalheObtem(id: string, recarrega: boolean = false): Observable<Pedido | null> {
    const cabecalhos = this.pedidoCabecalhos();
    if (!id || !cabecalhos) {
      return of(null);
    }
    // Cache por id. O conteudo de um pedido nao muda (itens, cliente, valores sao do
    // momento em que ele foi feito), entao rebuscar a cada atualizacao do quadro seria
    // desperdicio - e o quadro atualiza direto. So a `situacao` muda, e ela e aplicada
    // na saida, nunca guardada.
    if (!recarrega && this.pedidoDetalhes[id]) {
      return of(this.pedidoSituacaoAplica(this.pedidoDetalhes[id]));
    }
    // Silencioso: pelo `apiService`, falha de rede vira toast tecnico de 24s na tela.
    const url = this.apiService.getMapping(`aps://integracao/pedido/${id}`);
    return this._httpClient.get<Pedido>(url, { headers: cabecalhos }).pipe(
      map(pedido => {
        if (!pedido) {
          return null;
        }
        this.pedidoDetalhes[pedido.id] = pedido;
        return this.pedidoSituacaoAplica(pedido);
      }),
      catchError(() => of(null)),
    );
  }

  // Acompanha o pedido ate a retaguarda decidir e emite UMA vez: o motivo, se ele foi
  // recusado ou cancelado (ex.: 'O atendimento "Mesa 5" esta bloqueado', a frase do PDV),
  // ou vazio se foi aceito. A decisao e do PDV, que pode demorar - por isso consulta a
  // cada 10s enquanto houver quem escute; quem chama encerra com `takeUntil`.
  pedidoRecusaAcompanha(id: string): Observable<string> {
    if (!id) {
      return of('');
    }
    return timer(10000, 10000).pipe(
      switchMap(() => this.pedidoDetalheObtem(id, true)),
      filter(pedido => !!pedido && pedido.situacao !== PedidoSituacao.Pendente),
      first(),
      map(pedido => {
        if (!pedidoSituacaoNegada(pedido.situacao)) {
          return '';
        }
        return pedidoNegadaMotivo(pedido) || 'O estabelecimento recusou o pedido.';
      }),
    );
  }

  // A situacao avancada na tela de aceite vale mais que a do backend, igual no resto.
  private pedidoSituacaoAplica(pedido: Pedido): Pedido {
    const local = this.situacoesLocais()[pedido.id];
    return local ? ({ ...pedido, situacao: local } as Pedido) : pedido;
  }

  // O que ja esta em cache, sem disparar requisicao. O cartao usa isto para mostrar o
  // cliente assim que o detalhe chega, sem travar a renderizacao do quadro.
  pedidoDetalheEmCache(id: string): Pedido | null {
    return this.pedidoDetalhes[id] || null;
  }

  // Grava a nova situacao. Assinatura ja no formato final (Observable<boolean>), para
  // a troca pelo endpoint real nao mexer na tela.
  // `observacoes` e o motivo da recusa: no PATCH real vai no corpo, e o APS repassa ao
  // cliente no WhatsApp. Ate la fica guardado aqui, ao lado da situacao local.
  pedidoSituacaoDefine(id: string, situacao: PedidoSituacao, observacoes?: string): Observable<boolean> {
    if (!id) {
      return of(false);
    }
    const locais = this.situacoesLocais();
    locais[id] = situacao;
    this.apiService.setStorageData('situacao', this.situacoesChave(), locais);
    if (observacoes?.trim()) {
      const motivos = this.apiService.getStorageData('situacao', `${this.situacoesChave()}.motivo`) || {};
      motivos[id] = observacoes.trim();
      this.apiService.setStorageData('situacao', `${this.situacoesChave()}.motivo`, motivos);
    }
    return of(true);
  }

  // Zera os avancos locais e devolve o quadro ao que o backend diz. Ferramenta de
  // teste que anda com o status precisa saber voltar, senao o quadro vira lixo.
  // Apaga SO os avancos de situacao. Nao mexe nos ocultos de proposito: esconder pedido
  // antigo e curadoria do quadro, nao estado de teste - juntar as duas coisas fazia o
  // reset devolver centenas de pedidos que alguem tinha tirado da frente.
  pedidoSituacoesLimpa(): Observable<boolean> {
    this.apiService.setStorageData('situacao', this.situacoesChave(), {});
    return of(true);
  }

  pedidoOcultosLimpa(): Observable<boolean> {
    this.apiService.setStorageData('situacao', this.ocultosChave(), []);
    return of(true);
  }

  get pedidoOcultosQuantidade(): number {
    return this.ocultosLista().length;
  }

  // Grava a mesma situacao para varios pedidos de uma vez.
  pedidoSituacaoDefineVarios(ids: string[], situacao: PedidoSituacao): Observable<boolean> {
    const locais = this.situacoesLocais();
    ids.forEach(id => (locais[id] = situacao));
    this.apiService.setStorageData('situacao', this.situacoesChave(), locais);
    return of(true);
  }

  private pedidoHistoricoGrava(resumo: PedidoResumo): void {
    const dado = this.apiService.getStorageData('pedidos', this.pedidosChave());
    const historico = Array.isArray(dado) ? (dado as PedidoResumo[]) : [];
    this.apiService.setStorageData('pedidos', this.pedidosChave(), historicoAdiciona(historico, resumo));
  }

  private pedidoResumoMonta(pedido: Pedido, voucher?: Voucher): PedidoResumo {
    return {
      id: pedido.id || new Date().getTime().toString(),
      criadoEm: new Date().toISOString(),
      entrega: pedido.tipo === PedidoTipo.Delivery,
      mesa: this.localizador ? `${this.localizador.modalidade?.nome || ''} ${this.localizador.codigo || ''}`.trim() : '',
      quantidade: this.sacola.quantidade,
      subtotal: pedido.subtotal,
      frete: pedido.frete,
      desconto: pedido.desconto,
      cupom: voucher?.codigo || '',
      total: pedido.total,
      pagamento: pedido.pedidoPagamentos?.length ? pedido.pedidoPagamentos[0].formaNome : '',
      itens: this.sacola.linhasPrincipais.map(linha => ({
        nome: linha.item.nome,
        quantidade: linha.quantidade,
        ...(linha.item.observacoes?.trim() ? { observacoes: linha.item.observacoes.trim() } : {}),
      })),
    };
  }

  irParaPedidos() {
    const rota = pedidosRota(this.empresa?.id, this.estabelecimento?.id, this.localizador?.id, this.slug);
    if (!rota) {
      return;
    }
    this._router.navigate(rota);
  }

  irParaPerfil() {
    const rota = perfilRota(this.empresa?.id, this.estabelecimento?.id, this.localizador?.id, this.slug);
    if (!rota) {
      return;
    }
    this._router.navigate(rota);
  }

  irParaCheckout() {
    const rota = checkoutRota(this.empresa?.id, this.estabelecimento?.id, this.localizador?.id, this.slug);
    if (!rota) {
      return;
    }
    this._router.navigate(rota);
  }
}
